// ProviderScreen.js

import axios from "axios";
import React, { useState, useEffect } from "react";
import { API_IP } from "../env";
import ServicesOfProvidersFrom from "./Form/ServicesOfProvidersForm";
import NewProviderForm from "./Form/NewProviderForm";
import NewProviderRequestForm from "./Form/NewProviderRequestForm";
import ProviderDetailForm from "./Form/ProviderDetailForm";
import AskingForm from "./Form/AskingForm";

// Import react-bootstrap components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Badge from "react-bootstrap/Badge";
import Table from "react-bootstrap/Table"; // Already here, good.
import "./ProviderScreen.css";

function ProviderScreen() {
  const [refreshProvider, setRefreshProvider] = useState(false);
  const [providerData, SetProviderData] = useState([]);

  // State for which modal to show
  const [showServicesForm, setShowServicesForm] = useState(false);
  const [showNewProviderForm, setShowNewProviderForm] = useState(false);
  const [showRequestsForm, setShowRequestsForm] = useState(false);
  const [showDetailForm, setShowDetailForm] = useState(false);
  const [showAskingForm, setShowAskingForm] = useState(false);

  // Data for the modals
  const [selectedProviderId, setSelectedProviderId] = useState();
  const [selectedProviderName, setSelectedProviderName] = useState();

  const [newRequestCount, setRequestCount] = useState(0);

  // Use a string for the filter state, it's clearer
  const [filter, setFilter] = useState("verified"); // 'verified' or 'unverified'

  // Standardized API call function
  async function axiosReqConfig(url, method, data) {
    const bearerToken = localStorage.getItem("@uphealth_login_token");
    const reqConfig = {
      url: url,
      method: method,
      headers: { Authorization: `Bearer ${bearerToken}` },
      data: data,
    };
    return await axios(reqConfig);
  }

  // Fetch all providers
  async function getAllProviders() {
    const { data } = await axiosReqConfig(
      `${API_IP}/api/admin/providers`,
      "get",
      []
    );
    SetProviderData(data);
  }

  // Fetch new request count separately
  async function fetchRequestCount() {
    try {
      const { data } = await axiosReqConfig(
        `${API_IP}/api/admin/providers/requests`,
        "get",
        []
      );
      setRequestCount(data.length);
    } catch (err) {
      console.error("Could not fetch request count", err);
    }
  }

  useEffect(() => {
    getAllProviders();
    fetchRequestCount();
  }, [refreshProvider]);

  // --- Modal Open/Close Handlers ---
  const openDetailModal = (item) => {
    setSelectedProviderId(item.provider_id);
    setSelectedProviderName(item.provider_name);
    setShowDetailForm(true);
  };

  const openServicesModal = (item) => {
    setSelectedProviderId(item.provider_id);
    setSelectedProviderName(item.provider_name);
    setShowServicesForm(true);
  };

  const openDeleteModal = (item) => {
    setSelectedProviderId(item.provider_id);
    setSelectedProviderName(item.provider_name);
    setShowAskingForm(true);
  };

  // --- Render Item ---
  function Item({ item }) {
    // Determine if the item should be shown based on the filter
    const isVerified = item.verification === true && item.deactive === false;
    const isUnverified = item.verification === false && item.deactive === false;

    let show = false;
    if (filter === "verified" && isVerified) show = true;
    if (filter === "unverified" && isUnverified) show = true;

    if (!show) return null;

    return (
      <tr>
        <td className="fw-semibold">#{item.provider_id}</td>
        <td className="fw-bold">
          {item.provider_name} <br/>
          {item.verification ? (
            <Badge bg="primary" className="mt-1">
              Verified
            </Badge>
          ) : (
            <Badge bg="secondary" className="mt-1">
              Unverified
            </Badge>
          )}
        </td>
        <td>
          <Button
            variant="outline-dark"
            size="sm"
            className="action-btn"
            onClick={() => openDetailModal(item)}
          >
            <i className="bi bi-person-lines-fill me-1"></i> Details
          </Button>
        </td>
        <td>
          <Button
            variant="outline-dark"
            size="sm"
            className="action-btn"
            onClick={() => openServicesModal(item)}
          >
            <i className="bi bi-card-checklist me-1"></i> Services
          </Button>
        </td>
        <td>
          <Button
            variant="secondary"
            size="sm"
            className="action-btn"
            onClick={() => openDeleteModal(item)}
          >
            <i className="bi bi-trash me-1"></i> Delete
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <>
      <Container fluid className="provider-management-container px-0">
        {/* Header Row */}
        <Row className="mb-4 align-items-center card flex-row mx-0">
          <Col>
            <ButtonGroup className="filter-btn-group">
              <Button
                variant={filter === "verified" ? "primary" : "outline-primary"}
                onClick={() => setFilter("verified")}
              >
                Verified Providers
              </Button>
              <Button
                variant={
                  filter === "unverified" ? "primary" : "outline-primary"
                }
                onClick={() => setFilter("unverified")}
              >
                Unverified Providers
              </Button>
            </ButtonGroup>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="outline-dark" onClick={() => setShowRequestsForm(true)}>
              <i className="bi bi-bell-fill me-2"></i> Requests{" "}
              {newRequestCount > 0 && <Badge bg="secondary">{newRequestCount}</Badge>}
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowNewProviderForm(true)}
            >
              <i className="bi bi-plus-lg me-1"></i> New Provider
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <Row className="provider-table-wrapper card mx-0">
          <Col>
            <Table hover responsive className="provider-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Provider ID</th>
                  <th>Provider Name</th>
                  <th>Information</th>
                  <th>List Of Services</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {providerData.map((item) => (
                  <Item key={item.provider_id} item={item} />
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Container>

      {/* --- Modals --- */}
      {/* These now use the 'show' and 'onHide' props */}

      <AskingForm
        show={showAskingForm}
        onHide={() => setShowAskingForm(false)}
        providerID={selectedProviderId}
        providerName={selectedProviderName}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />

      <ServicesOfProvidersFrom
        show={showServicesForm}
        onHide={() => setShowServicesForm(false)}
        providerID={selectedProviderId}
        providerName={selectedProviderName}
      />

      <NewProviderForm
        show={showNewProviderForm}
        onHide={() => setShowNewProviderForm(false)}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />

      <NewProviderRequestForm
        show={showRequestsForm}
        onHide={() => setShowRequestsForm(false)}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />

      <ProviderDetailForm
        show={showDetailForm}
        onHide={() => setShowDetailForm(false)}
        providerID={selectedProviderId}
        providerName={selectedProviderName}
        setRefreshProvider={setRefreshProvider}
        refreshProvider={refreshProvider}
      />
    </>
  );
}

export default ProviderScreen;
